import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { login } from "../services/authService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { jwtDecode } from "jwt-decode";
import "../styles/user/Login.css"; // đã được scoped

const Login = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loginError, setLoginError] = useState("");
  const dropdownRef = useRef(null);

  // Hiệu ứng animation
  useEffect(() => {
    const timer = setTimeout(() => setAnimate(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Lấy danh sách tài khoản đã lưu
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("savedAccounts")) || [];
    const uniqueAccounts = stored.reduce((acc, curr) => {
      if (!acc.find((item) => item.email === curr.email)) acc.push(curr);
      return acc;
    }, []);
    setSavedAccounts(uniqueAccounts);

    if (uniqueAccounts.length > 0) {
      const last = uniqueAccounts[uniqueAccounts.length - 1];
      setForm({ email: last.email, password: last.password });
    }
  }, []);

  // Kiểm tra token đã đăng nhập chưa
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp > Date.now() / 1000) {
          if (decoded.role === "super_admin") {
            navigate("/super-admin/analytics/growth");
          } else if (decoded.role === "boss" || decoded.role === "assistant" || decoded.role === "admin") {
            navigate("/admin/dashboard");
          } else {
            navigate("/");
          }
        }
      } catch {
        localStorage.removeItem("token");
      }
    }
  }, [navigate]);

  // Ẩn dropdown khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (loginError) setLoginError("");
  };

  const handleSelectAccount = (acc) => {
    setForm({ email: acc.email, password: acc.password });
    setShowDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { token, user, refreshToken } = await login(form);

      if (rememberMe) {
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("refresh_token", refreshToken);

        let saved = JSON.parse(localStorage.getItem("savedAccounts")) || [];
        saved = saved.filter((acc) => acc.email !== form.email);
        saved.push({ email: form.email, password: form.password });
        localStorage.setItem("savedAccounts", JSON.stringify(saved));
      }
      toast.success("Đăng nhập thành công!", {
        position: "top-right",
        autoClose: 1000,
        theme: "colored",
        style: {
          background: "#3b5998 !important",
          color: "#fff",
          fontWeight: 600,
          borderRadius: "12px",
        },
        onClose: () => {
          if (user.role === "super_admin") {
            navigate("/super-admin/analytics/growth");
          } else if (user.role === "boss" || user.role === "assistant" || user.role === "admin") {
            navigate("/admin/dashboard");
          } else {
            navigate("/");
          }
        },
      });

    } catch (error) {
      const message = error?.error || error?.message || "Sai email hoặc mật khẩu!";
      setLoginError(message);
      toast.error(message, {
        position: "top-right",
        autoClose: 1000,
        theme: "colored",
        style: {
          background: "#111",
          color: "#fff",
          fontWeight: 600,
          borderRadius: "12px",
        },
      });
    }
  };

  return (
    <div className="login-page">
      {/* LEFT */}
      <div className="login-left">
        <img
          src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1500&q=80"
          alt="Modern apartment"
          className="login-image"
        />
        <div className="overlay"></div>
        <div className={`text-block ${animate ? "fade-in-up" : "hidden"}`}>
          <p className="sub-text">CHÀO MỪNG TRỞ LẠI</p>
          <h1 className="main-text">
            <span className="highlight">Khám phá</span> không gian sống
          </h1>
          <p className="tagline">
            Hiện đại, tiện nghi và lý tưởng dành cho bạn.
          </p>
          <button className="cta-btn" onClick={() => navigate("/")}>
            Khám phá ngay →
          </button>
        </div>
      </div>

      {/* RIGHT */}
      <div className="login-right">
        <form
          className={`login-form ${animate ? "scale-in" : "hidden"}`}
          onSubmit={handleSubmit}
        >
          <h2 className="title">Đăng nhập</h2>

          {/* EMAIL */}
          <div className="field" ref={dropdownRef}>
            <label>Email</label>
            <div className="input-wrapper">
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                autoComplete="off"
              />
              {savedAccounts.length > 0 && (
                <FontAwesomeIcon
                  icon={showDropdown ? faChevronUp : faChevronDown}
                  className="dropdown-icon"
                  onClick={() => setShowDropdown(!showDropdown)}
                />
              )}
              {showDropdown && (
                <ul className="dropdown-menu">
                  {savedAccounts.map((acc, index) => (
                    <li
                      key={index}
                      onClick={() => handleSelectAccount(acc)}
                      className="dropdown-item"
                    >
                      {acc.email}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* PASSWORD */}
          <div className="field">
            <label>Mật khẩu</label>
            <div className="input-wrapper">
              <input
                type={showPwd ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
              <FontAwesomeIcon
                icon={showPwd ? faEyeSlash : faEye}
                onClick={() => setShowPwd(!showPwd)}
                className="eye-icon"
              />
            </div>
          </div>

          {loginError && (
            <div className="login-error" role="alert">
              {loginError}
            </div>
          )}

          {/* REMEMBER ME */}
          <div className="checkbox-container">
            <div
              onClick={() => setRememberMe(!rememberMe)}
              className="custom-checkbox"
            >
              <div className={`custom-box ${rememberMe ? "checked" : ""}`}></div>
              {rememberMe && <div className="checkmark">✔</div>}
            </div>
            <label className="checkbox-label">Lưu thông tin đăng nhập</label>
          </div>

          <button type="submit" className="submit-btn">
            Đăng nhập
          </button>

          <p className="switch">
            Bạn chưa có tài khoản?{" "}
            <Link to="/register" className="link">
              Đăng ký ngay
            </Link>
          </p>
        </form>
      </div>

      <ToastContainer />
    </div>
  );
};

export default Login;
